<template>
  <div class="ai-consult-page">
    <!-- Header -->
    <header class="aic-header">
      <div class="aic-header-inner">
        <router-link :to="{ name: 'HomeSix' }" class="aic-logo">
          <img src="@/assets/images/logo.png" alt="AI Studio" />
        </router-link>
        <div class="aic-header-title">
          <h1>AI 기획 상담</h1>
          <p>아이디어를 입력하면 AI가 구체적인 기획으로 발전시켜 드립니다.</p>
        </div>
        <router-link :to="{ name: 'HomeSix' }" class="aic-back-home">
          <i class="fal fa-home"></i>
          <span>홈으로</span>
        </router-link>
      </div>
    </header>

    <!-- Main Content -->
    <main class="aic-main">
      <div class="aic-container">
        <!-- Step indicator -->
        <div class="aic-steps">
          <div v-for="(stepInfo, idx) in stepLabels" :key="idx" class="aic-step-item">
            <div
              class="aic-step"
              :class="{
                active: currentStep === idx + 1,
                done: currentStep > idx + 1,
                clickable: idx + 1 <= maxReachedStep
              }"
              @click="goToStep(idx + 1)"
            >
              <span class="aic-step-num">{{ idx + 1 }}</span>
              <span class="aic-step-label">{{ stepInfo }}</span>
            </div>
            <div v-if="idx < stepLabels.length - 1" class="aic-step-line" :class="{ active: currentStep > idx + 1 }"></div>
          </div>
        </div>

        <!-- Step 1: 아이디어 입력 -->
        <div v-show="currentStep === 1" class="aic-step-content">
          <div class="aic-card">
            <h2 class="aic-card-title">어떤 서비스를 만들고 싶으신가요?</h2>

            <div class="aic-field">
              <label>플랫폼 유형 <span class="req">*</span></label>
              <div class="aic-chip-group">
                <div
                  v-for="t in platformTypes"
                  :key="t.value"
                  class="aic-chip"
                  :class="{ active: ideaInput.platformType === t.value }"
                  @click="ideaInput.platformType = t.value"
                >{{ t.label }}</div>
              </div>
            </div>

            <div class="aic-field">
              <label>프로젝트명 <span class="optional">(선택)</span></label>
              <input
                type="text"
                v-model="ideaInput.projectName"
                placeholder="예: 우리동네 배달앱, 헬스케어 플랫폼"
              />
            </div>

            <div class="aic-field">
              <label>아이디어 <span class="req">*</span></label>
              <textarea
                v-model="ideaInput.prompt"
                placeholder="만들고 싶은 서비스에 대해 자유롭게 설명해 주세요.

예시:
- 동네 음식점 배달 앱을 만들고 싶어요. 사용자가 주문하고, 음식점이 주문을 받고, 배달원이 배달하는 기능이 필요해요.
- 헬스장 회원 관리 시스템이 필요해요. 회원 등록, 출석 체크, 결제 관리 기능이 있으면 좋겠어요."
                rows="8"
              ></textarea>
              <div class="aic-char-count">{{ ideaInput.prompt.length }} / 1000자</div>
            </div>

            <button class="aic-primary-btn" :disabled="!isIdeaComplete" @click="startAIAnalysis">
              <i class="fal fa-magic"></i> AI 분석 시작
            </button>
          </div>

          <!-- 최근 기획서 이력 -->
          <div v-if="proposalHistory.length > 0" class="aic-history-card">
            <div class="aic-history-header">
              <h3><i class="fal fa-history"></i> 최근 기획서 이력</h3>
              <span class="aic-history-count">최근 {{ proposalHistory.length }}개</span>
            </div>
            <div class="aic-history-items">
              <div
                v-for="(item, idx) in proposalHistory"
                :key="item.id"
                class="aic-history-item-card"
              >
                <div class="aic-history-item-info">
                  <div class="aic-history-item-title">
                    {{ item.projectName || '프로젝트 ' + (proposalHistory.length - idx) }}
                  </div>
                  <div class="aic-history-item-meta">
                    <span><i class="fal fa-calendar"></i> {{ formatDate(item.createdAt) }}</span>
                    <span><i class="fal fa-layer-group"></i> {{ item.platformLabel }}</span>
                    <span v-if="item.estimate"><i class="fal fa-won-sign"></i> {{ item.estimate.priceMin }}~{{ item.estimate.priceMax }}만원</span>
                  </div>
                  <div class="aic-history-item-summary">{{ truncateText(item.problemStatement, 60) }}</div>
                </div>
                <div class="aic-history-item-actions">
                  <button class="aic-history-view-btn" @click="viewHistoryProposal(item)">
                    <i class="fal fa-eye"></i> 보기
                  </button>
                  <button class="aic-history-use-btn" @click="useHistoryProposal(item)">
                    <i class="fal fa-arrow-right"></i> 상담 신청
                  </button>
                  <button class="aic-history-delete-btn" @click="deleteHistoryItem(item.id)">
                    <i class="fal fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: AI 분석 결과 -->
        <div v-show="currentStep === 2" class="aic-step-content">
          <div class="aic-card">
            <!-- Loading state -->
            <div v-if="aiElaboration.isLoading" class="aic-loading">
              <div class="aic-loading-icon">
                <i class="fal fa-robot"></i>
              </div>
              <p>AI가 아이디어를 분석하고 있습니다...</p>
              <div class="aic-streaming-box" v-if="aiElaboration.rawResponse">
                <pre>{{ aiElaboration.rawResponse }}</pre>
              </div>
            </div>

            <!-- Error state -->
            <div v-else-if="aiElaboration.error" class="aic-error">
              <i class="fal fa-exclamation-circle"></i>
              <p>{{ aiElaboration.error }}</p>
              <button class="aic-secondary-btn" @click="startAIAnalysis">
                <i class="fal fa-redo"></i> 다시 시도
              </button>
            </div>

            <!-- Result -->
            <div v-else-if="aiElaboration.parsedResult" class="aic-result">
              <h2 class="aic-card-title">AI 분석 결과</h2>

              <div class="aic-result-section">
                <h4><i class="fal fa-bullseye"></i> 서비스 목적</h4>
                <p>{{ aiElaboration.parsedResult.problemStatement }}</p>
              </div>

              <div class="aic-result-section">
                <h4><i class="fal fa-users"></i> 타겟 사용자</h4>
                <p>{{ aiElaboration.parsedResult.targetUsers }}</p>
              </div>

              <div class="aic-result-section">
                <h4><i class="fal fa-star"></i> 핵심 기능</h4>
                <ul>
                  <li v-for="feature in aiElaboration.parsedResult.coreFeatures" :key="feature.id">
                    <strong>{{ feature.name }}</strong>: {{ feature.description }}
                  </li>
                </ul>
              </div>

              <div class="aic-result-section" v-if="aiElaboration.parsedResult.optionalFeatures && aiElaboration.parsedResult.optionalFeatures.length">
                <h4><i class="fal fa-plus-circle"></i> 선택 기능</h4>
                <ul>
                  <li v-for="feature in aiElaboration.parsedResult.optionalFeatures" :key="feature.id">
                    <strong>{{ feature.name }}</strong>: {{ feature.description }}
                  </li>
                </ul>
              </div>

              <div class="aic-result-section">
                <h4><i class="fal fa-tachometer-alt"></i> 예상 복잡도</h4>
                <div class="aic-complexity" :class="aiElaboration.parsedResult.estimatedComplexity">
                  {{ getComplexityLabel(aiElaboration.parsedResult.estimatedComplexity) }}
                </div>
              </div>

              <!-- 피드백 입력 영역 -->
              <div class="aic-feedback-section">
                <h4><i class="fal fa-comment-dots"></i> 피드백 제공</h4>
                <p class="aic-feedback-desc">분석 결과에 대해 수정하거나 추가하고 싶은 내용이 있으면 알려주세요.</p>
                <textarea
                  v-model="feedback.analysisInput"
                  placeholder="예시:
- 결제 기능은 카카오페이만 지원하면 됩니다.
- 관리자용 대시보드도 필요합니다.
- 배달원 앱은 별도로 필요하지 않습니다."
                  rows="4"
                  class="aic-feedback-input"
                ></textarea>
                <button
                  class="aic-feedback-btn"
                  :disabled="!feedback.analysisInput.trim() || aiElaboration.isLoading"
                  @click="refineAnalysisWithFeedback"
                >
                  <i class="fal fa-sync-alt"></i> 피드백 반영하여 재분석
                </button>
              </div>
            </div>

            <div class="aic-btn-row" v-if="!aiElaboration.isLoading">
              <button class="aic-secondary-btn" @click="prevStep">
                <i class="fal fa-arrow-left"></i> 수정하기
              </button>
              <button class="aic-primary-btn" :disabled="!aiElaboration.parsedResult" @click="nextStep">
                모듈 선택하기 <i class="fal fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Step 3: 모듈 선택 -->
        <div v-show="currentStep === 3" class="aic-step-content">
          <div class="aic-card">
            <h2 class="aic-card-title">필요한 모듈을 선택하세요</h2>
            <p class="aic-card-desc">AI가 추천한 모듈을 확인하고, 필요에 따라 추가하거나 제외하세요.</p>

            <!-- 카테고리별 모듈 -->
            <div v-for="(modules, category) in getModulesByCategory()" :key="category" class="aic-module-section">
              <h4>{{ categoryLabels[category] }}</h4>
              <div class="aic-module-grid">
                <div
                  v-for="module in modules"
                  :key="module.id"
                  class="aic-module-card"
                  :class="{ selected: selectedModules.includes(module.id) }"
                  @click="toggleModule(module.id)"
                >
                  <div class="aic-module-header">
                    <i :class="module.icon"></i>
                    <span class="aic-module-name">{{ module.name }}</span>
                    <span class="aic-module-check"><i class="fal" :class="selectedModules.includes(module.id) ? 'fa-check-circle' : 'fa-circle'"></i></span>
                  </div>
                  <p class="aic-module-desc">{{ module.description }}</p>
                  <div class="aic-module-meta">
                    <span><i class="fal fa-won-sign"></i> {{ module.priceMin }}~{{ module.priceMax }}만원</span>
                    <span><i class="fal fa-clock"></i> {{ module.timelineDays }}일</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="aic-btn-row">
              <button class="aic-secondary-btn" @click="prevStep">
                <i class="fal fa-arrow-left"></i> 이전
              </button>
              <button class="aic-primary-btn" :disabled="selectedModules.length === 0" @click="calculateAndNext">
                견적 확인하기 <i class="fal fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Step 4: 견적 확인 -->
        <div v-show="currentStep === 4" class="aic-step-content">
          <div class="aic-card">
            <h2 class="aic-card-title">예상 견적</h2>

            <div v-if="estimate" class="aic-estimate">
              <div class="aic-estimate-summary">
                <div class="aic-estimate-item">
                  <label>예상 비용</label>
                  <div class="aic-estimate-value price">
                    {{ estimate.priceMin.toLocaleString() }} ~ {{ estimate.priceMax.toLocaleString() }}만원
                  </div>
                </div>
                <div class="aic-estimate-item">
                  <label>예상 기간</label>
                  <div class="aic-estimate-value">약 {{ estimate.timelineWeeks }}주</div>
                </div>
                <div class="aic-estimate-item">
                  <label>플랫폼</label>
                  <div class="aic-estimate-value">{{ getPlatformLabel() }}</div>
                </div>
              </div>

              <div class="aic-estimate-breakdown">
                <h4>상세 내역</h4>
                <table>
                  <thead>
                    <tr>
                      <th>항목</th>
                      <th>비용 (만원)</th>
                      <th>기간</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in estimate.breakdown" :key="item.id" :class="item.category">
                      <td>{{ item.name }}</td>
                      <td>{{ item.priceMin }}~{{ item.priceMax }}</td>
                      <td>{{ item.days }}일</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="aic-estimate-note">
                <i class="fal fa-info-circle"></i>
                위 견적은 AI가 분석한 예상 범위입니다. 실제 비용은 상세 상담 후 확정됩니다.
              </div>
            </div>

            <div class="aic-btn-row">
              <button class="aic-secondary-btn" @click="prevStep">
                <i class="fal fa-arrow-left"></i> 모듈 수정
              </button>
              <button class="aic-primary-btn" @click="generateAndNext">
                기획서 생성 <i class="fal fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Step 5: 기획서 검토 -->
        <div v-show="currentStep === 5" class="aic-step-content">
          <div class="aic-card">
            <h2 class="aic-card-title">AI 생성 기획서</h2>

            <div v-if="proposal.isLoading" class="aic-loading">
              <div class="aic-loading-icon">
                <i class="fal fa-file-alt"></i>
              </div>
              <p>기획서를 생성하고 있습니다...</p>
              <div class="aic-streaming-box" v-if="proposal.content">
                <div class="aic-markdown-viewer" v-html="renderMarkdown(proposal.content)"></div>
              </div>
            </div>

            <div v-else-if="proposal.error" class="aic-error">
              <i class="fal fa-exclamation-circle"></i>
              <p>{{ proposal.error }}</p>
              <button class="aic-secondary-btn" @click="generateProposal">
                <i class="fal fa-redo"></i> 다시 생성
              </button>
            </div>

            <div v-else class="aic-proposal">
              <!-- 마크다운 뷰어 -->
              <div class="aic-markdown-viewer" v-html="renderMarkdown(proposal.content)"></div>
              <!-- 전체보기 버튼 -->
              <button class="aic-fullview-btn" @click="showProposalPopup = true">
                <i class="fal fa-expand-alt"></i> 기획서 전체보기
              </button>
            </div>

            <!-- 기획서 피드백 영역 -->
            <div v-if="!proposal.isLoading && proposal.content" class="aic-feedback-section proposal-feedback">
              <h4><i class="fal fa-edit"></i> 기획서 수정 요청</h4>
              <p class="aic-feedback-desc">기획서 내용 중 수정하거나 보완이 필요한 부분이 있으면 알려주세요.</p>
              <textarea
                v-model="feedback.proposalInput"
                placeholder="예시:
- 예상 비용 부분에 유지보수 비용도 추가해주세요.
- 개발 일정을 좀 더 상세하게 나눠주세요.
- 보안 관련 내용을 더 강조해주세요."
                rows="4"
                class="aic-feedback-input"
              ></textarea>
              <button
                class="aic-feedback-btn"
                :disabled="!feedback.proposalInput.trim() || proposal.isLoading"
                @click="refineProposalWithFeedback"
              >
                <i class="fal fa-sync-alt"></i> 피드백 반영하여 기획서 수정
              </button>
            </div>

            <!-- 대화 히스토리 표시 -->
            <div v-if="conversationHistory.length > 0" class="aic-history-toggle">
              <button class="aic-history-btn" @click="showHistory = !showHistory">
                <i class="fal" :class="showHistory ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                피드백 히스토리 ({{ conversationHistory.length }}회)
              </button>
              <div v-if="showHistory" class="aic-history-list">
                <div v-for="(item, idx) in conversationHistory" :key="idx" class="aic-history-item">
                  <div class="aic-history-label">피드백 #{{ idx + 1 }}</div>
                  <div class="aic-history-content">{{ item.feedback }}</div>
                </div>
              </div>
            </div>

            <div class="aic-btn-row" v-if="!proposal.isLoading">
              <button class="aic-secondary-btn" @click="prevStep">
                <i class="fal fa-arrow-left"></i> 견적 확인
              </button>
              <button class="aic-primary-btn" :disabled="!proposal.content" @click="nextStep">
                상담 신청하기 <i class="fal fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Step 6: 연락처 입력 -->
        <div v-show="currentStep === 6" class="aic-step-content">
          <div class="aic-card">
            <h2 class="aic-card-title">상담 신청</h2>

            <div class="aic-field">
              <label>상담 유형</label>
              <div class="aic-chip-group">
                <div
                  class="aic-chip"
                  :class="{ active: contactInfo.consultingType === 'P' }"
                  @click="contactInfo.consultingType = 'P'"
                >개인</div>
                <div
                  class="aic-chip"
                  :class="{ active: contactInfo.consultingType === 'E' }"
                  @click="contactInfo.consultingType = 'E'"
                >기업 / 단체</div>
              </div>
            </div>

            <div class="aic-row">
              <div class="aic-field aic-half">
                <label>이름 <span class="req">*</span></label>
                <input
                  type="text"
                  v-model="contactInfo.name"
                  placeholder="담당자 이름"
                />
              </div>
              <div class="aic-field aic-half">
                <label>연락처 <span class="req">*</span></label>
                <input
                  type="tel"
                  v-model="contactInfo.phone"
                  placeholder="010-0000-0000"
                />
              </div>
            </div>

            <div class="aic-field">
              <label>이메일 <span class="req">*</span></label>
              <input
                type="email"
                v-model="contactInfo.email"
                placeholder="example@email.com"
              />
            </div>

            <div class="aic-field" v-if="contactInfo.consultingType === 'E'">
              <label>회사명</label>
              <input
                type="text"
                v-model="contactInfo.company"
                placeholder="회사/단체명"
              />
            </div>

            <!-- 요약 카드 -->
            <div class="aic-summary">
              <h5>상담 요약</h5>
              <div class="aic-summary-row">
                <span>플랫폼</span><strong>{{ getPlatformLabel() }}</strong>
              </div>
              <div class="aic-summary-row" v-if="ideaInput.projectName">
                <span>프로젝트명</span><strong>{{ ideaInput.projectName }}</strong>
              </div>
              <div class="aic-summary-row">
                <span>선택 모듈</span><strong>{{ selectedModules.length }}개</strong>
              </div>
              <div class="aic-summary-row" v-if="estimate">
                <span>예상 비용</span><strong>{{ estimate.priceMin }}~{{ estimate.priceMax }}만원</strong>
              </div>
              <div class="aic-summary-row" v-if="estimate">
                <span>예상 기간</span><strong>약 {{ estimate.timelineWeeks }}주</strong>
              </div>
            </div>

            <div class="aic-btn-row">
              <button class="aic-secondary-btn" @click="prevStep">
                <i class="fal fa-arrow-left"></i> 이전
              </button>
              <button class="aic-submit-btn" :disabled="!isContactComplete || isSubmitting" @click="submitConsultation">
                <span v-if="isSubmitting">전송 중...</span>
                <span v-else><i class="fal fa-paper-plane"></i> AI 기획 상담 신청</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Loading overlay -->
    <div v-if="isSubmitting" class="aic-loading-overlay">
      <div class="aic-spinner"></div>
    </div>

    <!-- 기획서 전체보기 팝업 -->
    <div v-if="showProposalPopup" class="proposal-popup-overlay" @click.self="showProposalPopup = false">
      <div class="proposal-popup">
        <div class="proposal-popup-header">
          <h3><i class="fal fa-file-alt"></i> AI 생성 기획서</h3>
          <button class="proposal-popup-close" @click="showProposalPopup = false">
            <i class="fal fa-times"></i>
          </button>
        </div>
        <div class="proposal-popup-body">
          <div class="proposal-popup-content" v-html="renderMarkdown(proposal.content)"></div>
        </div>
        <div class="proposal-popup-footer">
          <button class="aic-secondary-btn" @click="showProposalPopup = false">
            <i class="fal fa-times"></i> 닫기
          </button>
        </div>
      </div>
    </div>

    <!-- 이력 기획서 보기 팝업 -->
    <div v-if="showHistoryPopup" class="proposal-popup-overlay" @click.self="showHistoryPopup = false">
      <div class="proposal-popup">
        <div class="proposal-popup-header">
          <h3><i class="fal fa-history"></i> {{ selectedHistoryItem?.projectName || '저장된 기획서' }}</h3>
          <button class="proposal-popup-close" @click="showHistoryPopup = false">
            <i class="fal fa-times"></i>
          </button>
        </div>
        <div class="proposal-popup-body">
          <div class="proposal-popup-content" v-html="renderMarkdown(selectedHistoryItem?.proposalContent || '')"></div>
        </div>
        <div class="proposal-popup-footer">
          <button class="aic-secondary-btn" @click="showHistoryPopup = false">
            <i class="fal fa-times"></i> 닫기
          </button>
          <button class="aic-primary-btn" @click="useHistoryProposal(selectedHistoryItem); showHistoryPopup = false">
            <i class="fal fa-arrow-right"></i> 이 기획서로 상담 신청
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { marked } from 'marked';
import { invokeBedrockStream } from '@/services/BedrockService';
import { getModulesByCategory as getModules, calculateEstimate, MODULE_CATALOG } from '@/constants/modulesPricing';
import {
  createIdeaMessage,
  createProposalMessage
} from '@/constants/prompts';

// 백엔드 API 베이스 URL (개발/프로덕션 동일)
const API_BASE_URL = 'https://mapi.swing2app.co.kr';

// marked 설정
marked.setOptions({
  breaks: true,
  gfm: true
});

export default {
  name: 'AIConsult',
  data() {
    return {
      currentStep: 1,
      maxReachedStep: 1,
      stepLabels: ['아이디어', 'AI 분석', '모듈 선택', '견적', '기획서', '연락처'],
      platformTypes: [
        { value: 'app', label: '앱' },
        { value: 'web', label: '웹' },
        { value: 'both', label: '웹 + 앱' },
        { value: 'unsure', label: '잘 모르겠어요' }
      ],
      categoryLabels: {
        core: '핵심 모듈',
        optional: '선택 모듈',
        advanced: '고급 모듈'
      },
      ideaInput: {
        prompt: '',
        platformType: '',
        projectName: ''
      },
      aiElaboration: {
        rawResponse: '',
        isLoading: false,
        error: null,
        parsedResult: null
      },
      selectedModules: [],
      estimate: null,
      proposal: {
        content: '',
        isLoading: false,
        error: null
      },
      contactInfo: {
        consultingType: 'P',
        name: '',
        phone: '',
        email: '',
        company: ''
      },
      isSubmitting: false,
      feedback: {
        analysisInput: '',
        proposalInput: ''
      },
      conversationHistory: [],
      showHistory: false,
      showProposalPopup: false,
      // 기획서 이력 관련
      proposalHistory: [],
      showHistoryPopup: false,
      selectedHistoryItem: null
    };
  },
  computed: {
    isIdeaComplete() {
      return this.ideaInput.prompt.trim().length > 10 && this.ideaInput.platformType;
    },
    isContactComplete() {
      const { name, phone, email } = this.contactInfo;
      return name.trim() && phone.trim() && email.trim();
    }
  },
  mounted() {
    // LocalStorage에서 기획서 이력 불러오기
    this.loadProposalHistory();
  },
  methods: {
    // ============================================
    // 기획서 이력 관리 (LocalStorage)
    // ============================================
    loadProposalHistory() {
      try {
        const saved = localStorage.getItem('ai_proposal_history');
        if (saved) {
          this.proposalHistory = JSON.parse(saved);
        }
      } catch (e) {
        console.error('기획서 이력 불러오기 실패:', e);
        this.proposalHistory = [];
      }
    },
    saveProposalToHistory() {
      // 현재 기획서 정보를 이력에 저장
      const historyItem = {
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        projectName: this.ideaInput.projectName || '',
        platformType: this.ideaInput.platformType,
        platformLabel: this.getPlatformLabel(),
        ideaPrompt: this.ideaInput.prompt,
        problemStatement: this.aiElaboration.parsedResult?.problemStatement || '',
        targetUsers: this.aiElaboration.parsedResult?.targetUsers || '',
        coreFeatures: this.aiElaboration.parsedResult?.coreFeatures || [],
        optionalFeatures: this.aiElaboration.parsedResult?.optionalFeatures || [],
        selectedModules: [...this.selectedModules],
        estimate: this.estimate ? { ...this.estimate } : null,
        proposalContent: this.proposal.content
      };

      // 기존 이력 앞에 추가 (최신 순)
      this.proposalHistory.unshift(historyItem);

      // 최대 5개만 유지
      if (this.proposalHistory.length > 5) {
        this.proposalHistory = this.proposalHistory.slice(0, 5);
      }

      // LocalStorage에 저장
      try {
        localStorage.setItem('ai_proposal_history', JSON.stringify(this.proposalHistory));
      } catch (e) {
        console.error('기획서 이력 저장 실패:', e);
      }
    },
    viewHistoryProposal(item) {
      this.selectedHistoryItem = item;
      this.showHistoryPopup = true;
    },
    useHistoryProposal(item) {
      // 이력 데이터로 상태 복원
      this.ideaInput.projectName = item.projectName || '';
      this.ideaInput.platformType = item.platformType;
      this.ideaInput.prompt = item.ideaPrompt;

      this.aiElaboration.parsedResult = {
        problemStatement: item.problemStatement,
        targetUsers: item.targetUsers,
        coreFeatures: item.coreFeatures,
        optionalFeatures: item.optionalFeatures,
        estimatedComplexity: item.estimate?.complexity || 'medium'
      };
      this.aiElaboration.rawResponse = '';
      this.aiElaboration.isLoading = false;
      this.aiElaboration.error = null;

      this.selectedModules = item.selectedModules || [];
      this.estimate = item.estimate;
      this.proposal.content = item.proposalContent;
      this.proposal.isLoading = false;
      this.proposal.error = null;

      // Step 6 (연락처 입력)으로 이동
      this.maxReachedStep = 6;
      this.currentStep = 6;
      window.scrollTo(0, 0);
    },
    deleteHistoryItem(id) {
      if (!confirm('이 기획서 이력을 삭제하시겠습니까?')) return;

      this.proposalHistory = this.proposalHistory.filter(item => item.id !== id);

      try {
        localStorage.setItem('ai_proposal_history', JSON.stringify(this.proposalHistory));
      } catch (e) {
        console.error('기획서 이력 삭제 실패:', e);
      }
    },
    formatDate(isoString) {
      const date = new Date(isoString);
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${month}/${day} ${hours}:${minutes}`;
    },
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    // ============================================
    // 기존 메서드들
    // ============================================
    nextStep() {
      if (this.currentStep < 6) {
        this.currentStep++;
        if (this.currentStep > this.maxReachedStep) {
          this.maxReachedStep = this.currentStep;
        }
        window.scrollTo(0, 0);
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        window.scrollTo(0, 0);
      }
    },
    goToStep(step) {
      if (step >= 1 && step <= this.maxReachedStep) {
        this.currentStep = step;
        window.scrollTo(0, 0);
      }
    },
    toggleModule(moduleId) {
      const index = this.selectedModules.indexOf(moduleId);
      if (index === -1) {
        this.selectedModules.push(moduleId);
      } else {
        this.selectedModules.splice(index, 1);
      }
    },
    getModulesByCategory() {
      return getModules();
    },
    getComplexityLabel(complexity) {
      const map = {
        simple: '단순 (MVP 수준)',
        medium: '보통 (일반 서비스)',
        complex: '복잡 (고급 기능 포함)'
      };
      return map[complexity] || '';
    },
    getPlatformLabel() {
      const t = this.platformTypes.find(t => t.value === this.ideaInput.platformType);
      return t ? t.label : '';
    },
    renderMarkdown(content) {
      if (!content) return '';
      try {
        return marked.parse(content);
      } catch (e) {
        console.error('마크다운 파싱 오류:', e);
        return content;
      }
    },
    // GTM 이벤트 전송 헬퍼
    pushGTMEvent(eventName) {
      if (window.dataLayer) {
        var gaEventOb = {};
        gaEventOb.event = eventName;
        window.dataLayer.push(gaEventOb);
        console.log('[GTM] Event pushed:', eventName);
      }
    },
    async startAIAnalysis() {
      if (!this.isIdeaComplete) return;

      // GTM 이벤트: Step 1 완료 (AI 분석 시작)
      this.pushGTMEvent('ai-studio-landing-do-step1');

      this.nextStep();
      this.aiElaboration.isLoading = true;
      this.aiElaboration.rawResponse = '';
      this.aiElaboration.error = null;
      this.aiElaboration.parsedResult = null;

      try {
        const userMessage = createIdeaMessage(this.ideaInput);

        const response = await invokeBedrockStream(
          [{ role: 'user', content: userMessage }],
          {
            requestType: 'idea_analysis',  // 아이디어 분석용 프롬프트 사용
            onChunk: (chunk) => {
              this.aiElaboration.rawResponse += chunk;
            },
            userIdea: this.ideaInput.prompt,
            projectName: this.ideaInput.projectName
          }
        );

        this.aiElaboration.isLoading = false;

        const parsed = this.parseJsonFromResponse(response.content);
        if (parsed) {
          this.aiElaboration.parsedResult = parsed;

          if (parsed.suggestedModules) {
            this.selectedModules = parsed.suggestedModules
              .map(sm => sm.moduleId)
              .filter(id => MODULE_CATALOG[id]);
          }
          // GTM 이벤트: Step 2 완료 (AI 분석 완료)
          this.pushGTMEvent('ai-studio-landing-do-step2');
        } else {
          this.aiElaboration.error = 'AI 응답을 파싱할 수 없습니다.';
        }
      } catch (error) {
        console.error('AI 분석 실패:', error);
        this.aiElaboration.isLoading = false;
        this.aiElaboration.error = error.message;
      }
    },
    async refineAnalysisWithFeedback() {
      const feedbackText = this.feedback.analysisInput.trim();
      if (!feedbackText) return;

      this.aiElaboration.isLoading = true;
      this.aiElaboration.rawResponse = '';
      this.aiElaboration.error = null;

      try {
        const previousResult = JSON.stringify(this.aiElaboration.parsedResult, null, 2);
        const userMessage = `## 이전 분석 결과
\`\`\`json
${previousResult}
\`\`\`

## 사용자 피드백
${feedbackText}

위 피드백을 반영하여 분석 결과를 수정해주세요. 동일한 JSON 형식으로 응답해주세요.`;

        const response = await invokeBedrockStream(
          [{ role: 'user', content: userMessage }],
          {
            requestType: 'idea_analysis',  // 아이디어 분석용 프롬프트 사용
            onChunk: (chunk) => {
              this.aiElaboration.rawResponse += chunk;
            },
            userIdea: this.ideaInput.prompt,
            projectName: this.ideaInput.projectName
          }
        );

        this.aiElaboration.isLoading = false;

        const parsed = this.parseJsonFromResponse(response.content);
        if (parsed) {
          this.aiElaboration.parsedResult = parsed;

          if (parsed.suggestedModules) {
            this.selectedModules = parsed.suggestedModules
              .map(sm => sm.moduleId)
              .filter(id => MODULE_CATALOG[id]);
          }

          this.conversationHistory.push({
            type: 'analysis',
            feedback: feedbackText,
            timestamp: new Date().toISOString()
          });

          this.feedback.analysisInput = '';
        } else {
          this.aiElaboration.error = 'AI 응답을 파싱할 수 없습니다.';
        }
      } catch (error) {
        console.error('피드백 반영 실패:', error);
        this.aiElaboration.isLoading = false;
        this.aiElaboration.error = error.message;
      }
    },
    async refineProposalWithFeedback() {
      const feedbackText = this.feedback.proposalInput.trim();
      if (!feedbackText) return;

      this.proposal.isLoading = true;
      const previousContent = this.proposal.content;
      this.proposal.content = '';
      this.proposal.error = null;

      try {
        const userMessage = `## 현재 기획서
${previousContent}

## 수정 요청사항
${feedbackText}

위 피드백을 반영하여 기획서를 수정해주세요. 마크다운 형식으로 응답해주세요.`;

        await invokeBedrockStream(
          [{ role: 'user', content: userMessage }],
          {
            requestType: 'proposal',  // 기획서 생성용 프롬프트 사용
            onChunk: (chunk) => {
              this.proposal.content += chunk;
            },
            userIdea: this.ideaInput.prompt,
            projectName: this.ideaInput.projectName
          }
        );

        this.proposal.isLoading = false;

        this.conversationHistory.push({
          type: 'proposal',
          feedback: feedbackText,
          timestamp: new Date().toISOString()
        });

        this.feedback.proposalInput = '';
      } catch (error) {
        console.error('기획서 수정 실패:', error);
        this.proposal.isLoading = false;
        this.proposal.content = previousContent;
        this.proposal.error = error.message;
      }
    },
    parseJsonFromResponse(content) {
      try {
        const jsonMatch = content.match(/```json\s*([\s\S]*?)```/);
        if (jsonMatch) {
          return JSON.parse(jsonMatch[1]);
        }
        return JSON.parse(content);
      } catch (e) {
        console.error('JSON 파싱 실패:', e);
        return null;
      }
    },
    calculateAndNext() {
      this.estimate = calculateEstimate(
        this.selectedModules,
        this.ideaInput.platformType,
        this.aiElaboration.parsedResult?.estimatedComplexity || 'medium'
      );
      // GTM 이벤트: Step 3 완료 (모듈 선택 완료)
      this.pushGTMEvent('ai-studio-landing-do-step3');
      this.nextStep();
    },
    async generateAndNext() {
      this.nextStep();
      await this.generateProposal();
    },
    async generateProposal() {
      this.proposal.isLoading = true;
      this.proposal.content = '';
      this.proposal.error = null;

      try {
        const data = {
          problemStatement: this.aiElaboration.parsedResult?.problemStatement || '',
          targetUsers: this.aiElaboration.parsedResult?.targetUsers || '',
          coreFeatures: this.aiElaboration.parsedResult?.coreFeatures || [],
          optionalFeatures: this.aiElaboration.parsedResult?.optionalFeatures || [],
          selectedModules: this.selectedModules,
          platformType: this.ideaInput.platformType,
          estimate: this.estimate
        };

        const userMessage = createProposalMessage(data);

        await invokeBedrockStream(
          [{ role: 'user', content: userMessage }],
          {
            requestType: 'proposal',  // 기획서 생성용 프롬프트 사용
            onChunk: (chunk) => {
              this.proposal.content += chunk;
            },
            userIdea: this.ideaInput.prompt,
            projectName: this.ideaInput.projectName
          }
        );

        this.proposal.isLoading = false;

        // GTM 이벤트: 기획서 생성 완료
        this.pushGTMEvent('ai-studio-landing-create-doc');

        // 기획서 생성 완료 시 이력에 저장
        this.saveProposalToHistory();
      } catch (error) {
        console.error('기획서 생성 실패:', error);
        this.proposal.isLoading = false;
        this.proposal.error = error.message;
      }
    },
    async submitConsultation() {
      this.isSubmitting = true;

      try {
        const submitData = {
          ideaInput: this.ideaInput,
          elaboration: this.aiElaboration.parsedResult,
          selectedModules: this.selectedModules,
          estimate: this.estimate,
          proposal: this.proposal.content,
          contactInfo: this.contactInfo,
          conversationHistory: this.conversationHistory
        };

        await this.sendToTeamsEmail(submitData);

        // GTM 이벤트: 상담 신청 완료
        this.pushGTMEvent('ai-studio-landing-do-request-consult');

        this.isSubmitting = false;
        alert('AI 기획 상담 신청이 완료되었습니다.\n빠른 시간 내에 연락드리겠습니다.');
        this.$router.push({ name: 'HomeSix' });
      } catch (error) {
        console.error('제출 실패:', error);
        this.isSubmitting = false;
        alert('오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
      }
    },
    async sendToTeamsEmail(data) {
      const teamsEmail = '0264efec.swing2app.com@kr.teams.ms';

      const moduleDetails = data.selectedModules
        .map(id => {
          const m = MODULE_CATALOG[id];
          return m ? `- ${m.name} (${m.priceMin}~${m.priceMax}만원, ${m.timelineDays}일)` : id;
        })
        .join('\n');

      const platformLabel = this.getPlatformLabel();
      const consultingTypeLabel = data.contactInfo.consultingType === 'P' ? '개인' : '기업/단체';

      const coreFeaturesList = (data.elaboration?.coreFeatures || [])
        .map(f => `- ${f.name}: ${f.description}`)
        .join('\n') || '없음';

      const optionalFeaturesList = (data.elaboration?.optionalFeatures || [])
        .map(f => `- ${f.name}: ${f.description}`)
        .join('\n') || '없음';

      const estimateBreakdown = (data.estimate?.breakdown || [])
        .map(item => `- ${item.name}: ${item.priceMin}~${item.priceMax}만원 (${item.days}일)`)
        .join('\n') || '없음';

      const complexityLabel = {
        simple: '단순 (MVP 수준)',
        medium: '보통 (일반 서비스)',
        complex: '복잡 (고급 기능 포함)'
      }[data.elaboration?.estimatedComplexity] || '';

      const feedbackHistoryText = (data.conversationHistory || [])
        .map((item, idx) => {
          const typeLabel = item.type === 'analysis' ? '분석 피드백' : '기획서 피드백';
          return `[${idx + 1}] ${typeLabel}: ${item.feedback}`;
        })
        .join('\n\n') || '없음';

      const subject = `[AI 기획 상담] ${data.ideaInput.projectName || '신규 프로젝트'} - ${data.contactInfo.name}`;

      const htmlContent = `
<h2>AI 기획 상담 신청</h2>

<h3>연락처 정보</h3>
<table style="border-collapse: collapse; width: 100%; max-width: 600px;">
  <tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>상담유형</strong></td><td style="padding: 8px; border: 1px solid #ddd;">${consultingTypeLabel}</td></tr>
  <tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>이름</strong></td><td style="padding: 8px; border: 1px solid #ddd;">${data.contactInfo.name}</td></tr>
  <tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>연락처</strong></td><td style="padding: 8px; border: 1px solid #ddd;">${data.contactInfo.phone}</td></tr>
  <tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>이메일</strong></td><td style="padding: 8px; border: 1px solid #ddd;">${data.contactInfo.email}</td></tr>
  ${data.contactInfo.company ? `<tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>회사명</strong></td><td style="padding: 8px; border: 1px solid #ddd;">${data.contactInfo.company}</td></tr>` : ''}
</table>

<h3>프로젝트 개요</h3>
<table style="border-collapse: collapse; width: 100%; max-width: 600px;">
  <tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>프로젝트명</strong></td><td style="padding: 8px; border: 1px solid #ddd;">${data.ideaInput.projectName || '미정'}</td></tr>
  <tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>플랫폼</strong></td><td style="padding: 8px; border: 1px solid #ddd;">${platformLabel}</td></tr>
  <tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>예상 복잡도</strong></td><td style="padding: 8px; border: 1px solid #ddd;">${complexityLabel}</td></tr>
</table>

<h3>사용자 아이디어 원문</h3>
<div style="background: #f5f5f5; padding: 15px; border-radius: 8px; white-space: pre-wrap;">${data.ideaInput.prompt}</div>

<h3>AI 분석 결과</h3>
<p><strong>서비스 목적:</strong> ${data.elaboration?.problemStatement || ''}</p>
<p><strong>타겟 사용자:</strong> ${data.elaboration?.targetUsers || ''}</p>

<h3>핵심 기능</h3>
<pre style="background: #f5f5f5; padding: 15px; border-radius: 8px;">${coreFeaturesList}</pre>

<h3>선택 기능</h3>
<pre style="background: #f5f5f5; padding: 15px; border-radius: 8px;">${optionalFeaturesList}</pre>

<h3>선택된 모듈 상세</h3>
<pre style="background: #f5f5f5; padding: 15px; border-radius: 8px;">${moduleDetails || '없음'}</pre>

<h3>견적 상세</h3>
<table style="border-collapse: collapse; width: 100%; max-width: 600px;">
  <tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>총 예상 비용</strong></td><td style="padding: 8px; border: 1px solid #ddd;">${data.estimate?.priceMin || 0}~${data.estimate?.priceMax || 0}만원</td></tr>
  <tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>총 예상 기간</strong></td><td style="padding: 8px; border: 1px solid #ddd;">약 ${data.estimate?.timelineWeeks || 0}주 (${data.estimate?.totalDays || 0}일)</td></tr>
</table>

<h4>견적 내역</h4>
<pre style="background: #f5f5f5; padding: 15px; border-radius: 8px; font-size: 12px;">${estimateBreakdown}</pre>

<h3>사용자 피드백 히스토리</h3>
<p>피드백 횟수: ${(data.conversationHistory || []).length}회</p>
<pre style="background: #f5f5f5; padding: 15px; border-radius: 8px;">${feedbackHistoryText}</pre>

<hr>
<p style="color: #666; font-size: 12px;">상세 기획서는 첨부파일(MD)을 확인해주세요.</p>
`;

      const textContent = `AI 기획 상담 신청

[연락처 정보]
상담유형: ${consultingTypeLabel}
이름: ${data.contactInfo.name}
연락처: ${data.contactInfo.phone}
이메일: ${data.contactInfo.email}
${data.contactInfo.company ? `회사명: ${data.contactInfo.company}` : ''}

[프로젝트 개요]
프로젝트명: ${data.ideaInput.projectName || '미정'}
플랫폼: ${platformLabel}
예상 복잡도: ${complexityLabel}

[사용자 아이디어 원문]
${data.ideaInput.prompt}

[AI 분석 결과]
서비스 목적: ${data.elaboration?.problemStatement || ''}
타겟 사용자: ${data.elaboration?.targetUsers || ''}

[핵심 기능]
${coreFeaturesList}

[선택 기능]
${optionalFeaturesList}

[선택된 모듈 상세]
${moduleDetails || '없음'}

[견적 상세]
총 예상 비용: ${data.estimate?.priceMin || 0}~${data.estimate?.priceMax || 0}만원
총 예상 기간: 약 ${data.estimate?.timelineWeeks || 0}주 (${data.estimate?.totalDays || 0}일)

[피드백 히스토리]
${feedbackHistoryText}

상세 기획서는 첨부파일(MD)을 확인해주세요.
`;

      const response = await axios.post(`${API_BASE_URL}/api/email/teams`, {
        toEmail: teamsEmail,
        subject: subject,
        htmlContent: htmlContent,
        textContent: textContent,
        proposalMarkdown: data.proposal,
        projectName: data.ideaInput.projectName
      });
      console.log('Teams 이메일 전송 성공:', response.data);
      return response;
    }
  }
};
</script>

<style scoped>
.ai-consult-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
}

/* Header */
.aic-header {
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;
}

.aic-header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 24px;
}

.aic-logo img {
  height: 36px;
}

.aic-header-title {
  flex: 1;
}

.aic-header-title h1 {
  font-size: 20px;
  font-weight: 700;
  color: #0e1133;
  margin: 0;
}

.aic-header-title p {
  font-size: 13px;
  color: #7a7a7a;
  margin: 2px 0 0;
}

.aic-back-home {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #505056;
  text-decoration: none;
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s;
}

.aic-back-home:hover {
  background: #f5f5f7;
  color: #0a44b9;
}

/* Main */
.aic-main {
  padding: 32px 24px 60px;
}

.aic-container {
  max-width: 800px;
  margin: 0 auto;
}

/* Steps */
.aic-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 4px;
  background: #fff;
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.aic-step-item {
  display: flex;
  align-items: center;
}

.aic-step {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: default;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.2s;
}

.aic-step.clickable {
  cursor: pointer;
}

.aic-step.clickable:hover {
  background: #f5f5f7;
}

.aic-step-num {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e8e8e8;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
}

.aic-step.active .aic-step-num,
.aic-step.done .aic-step-num {
  background: #0a44b9;
  color: #fff;
}

.aic-step-label {
  font-size: 14px;
  color: #999;
  font-weight: 500;
}

.aic-step.active .aic-step-label {
  color: #0e1133;
  font-weight: 600;
}

.aic-step.done .aic-step-label {
  color: #0a44b9;
}

.aic-step-line {
  width: 24px;
  height: 2px;
  background: #e8e8e8;
  margin: 0 4px;
}

.aic-step-line.active {
  background: #0a44b9;
}

/* Card */
.aic-card {
  background: #fff;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.aic-card-title {
  font-size: 24px;
  font-weight: 700;
  color: #0e1133;
  margin: 0 0 8px;
}

.aic-card-desc {
  font-size: 15px;
  color: #7a7a7a;
  margin: 0 0 24px;
}

/* Fields */
.aic-field {
  margin-bottom: 20px;
}

.aic-field label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #0e1133;
  margin-bottom: 8px;
}

.aic-field label .req {
  color: #0a44b9;
}

.aic-field label .optional {
  color: #aaa;
  font-weight: 400;
}

.aic-field input[type="text"],
.aic-field input[type="email"],
.aic-field input[type="tel"],
.aic-field textarea {
  width: 100%;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 15px;
  outline: none;
  box-sizing: border-box;
  background: #fff;
  font-family: inherit;
  transition: border-color 0.2s;
}

.aic-field input:focus,
.aic-field textarea:focus {
  border-color: #0a44b9;
}

.aic-field textarea {
  resize: vertical;
  min-height: 160px;
}

.aic-char-count {
  text-align: right;
  font-size: 12px;
  color: #aaa;
  margin-top: 6px;
}

.aic-row {
  display: flex;
  gap: 16px;
}

.aic-half {
  flex: 1;
}

/* Chips */
.aic-chip-group {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.aic-chip {
  padding: 12px 24px;
  border: 2px solid #e8e8e8;
  border-radius: 25px;
  font-size: 15px;
  cursor: pointer;
  color: #505056;
  background: #fff;
  transition: all 0.2s;
}

.aic-chip:hover {
  border-color: #0a44b9;
  color: #0a44b9;
}

.aic-chip.active {
  background: #0a44b9;
  border-color: #0a44b9;
  color: #fff;
  font-weight: 600;
}

/* Buttons */
.aic-primary-btn {
  width: 100%;
  background: linear-gradient(135deg, #0a44b9 0%, #1e5fd9 100%);
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 16px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s;
  box-shadow: 0 4px 15px rgba(10, 68, 185, 0.3);
}

.aic-primary-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(10, 68, 185, 0.4);
}

.aic-primary-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}

.aic-secondary-btn {
  background: #f5f5f7;
  color: #505056;
  border: none;
  border-radius: 14px;
  padding: 16px 28px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.aic-secondary-btn:hover {
  background: #eaeaec;
}

.aic-submit-btn {
  flex: 1;
  background: linear-gradient(135deg, #0a44b9 0%, #1e5fd9 100%);
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 16px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 15px rgba(10, 68, 185, 0.3);
}

.aic-submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(10, 68, 185, 0.4);
}

.aic-submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}

.aic-btn-row {
  display: flex;
  gap: 16px;
  margin-top: 24px;
}

.aic-btn-row .aic-primary-btn {
  flex: 1;
  margin-top: 0;
}

/* Loading */
.aic-loading {
  text-align: center;
  padding: 48px 20px;
}

.aic-loading-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #0a44b9, #6366f1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  animation: pulse 1.5s ease-in-out infinite;
}

.aic-loading-icon i {
  font-size: 36px;
  color: #fff;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.aic-loading p {
  font-size: 17px;
  color: #505056;
  margin: 0 0 20px;
}

.aic-streaming-box {
  background: #f8f9fb;
  border-radius: 12px;
  padding: 20px;
  text-align: left;
  max-height: 400px;
  overflow-y: auto;
}

.aic-streaming-box pre {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  color: #505056;
  margin: 0;
  font-family: inherit;
}

/* Error */
.aic-error {
  text-align: center;
  padding: 48px 20px;
}

.aic-error i {
  font-size: 56px;
  color: #ef4444;
  margin-bottom: 20px;
}

.aic-error p {
  font-size: 15px;
  color: #505056;
  margin: 0 0 20px;
}

/* Result */
.aic-result-section {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
}

.aic-result-section:last-of-type {
  border-bottom: none;
}

.aic-result-section h4 {
  font-size: 16px;
  font-weight: 600;
  color: #0e1133;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.aic-result-section h4 i {
  color: #0a44b9;
  font-size: 18px;
}

.aic-result-section p {
  font-size: 15px;
  color: #505056;
  margin: 0;
  line-height: 1.7;
}

.aic-result-section ul {
  margin: 0;
  padding-left: 20px;
}

.aic-result-section li {
  font-size: 15px;
  color: #505056;
  margin-bottom: 8px;
  line-height: 1.6;
}

.aic-complexity {
  display: inline-block;
  padding: 8px 18px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
}

.aic-complexity.simple {
  background: #d1fae5;
  color: #065f46;
}

.aic-complexity.medium {
  background: #fef3c7;
  color: #92400e;
}

.aic-complexity.complex {
  background: #fee2e2;
  color: #991b1b;
}

/* Feedback */
.aic-feedback-section {
  margin-top: 28px;
  padding: 20px;
  background: #f0f7ff;
  border-radius: 14px;
  border: 1px solid #d0e3ff;
}

.aic-feedback-section h4 {
  font-size: 15px;
  font-weight: 600;
  color: #0e1133;
  margin: 0 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.aic-feedback-section h4 i {
  color: #0a44b9;
}

.aic-feedback-desc {
  font-size: 13px;
  color: #666;
  margin: 0 0 14px;
}

.aic-feedback-input {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 14px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  box-sizing: border-box;
  background: #fff;
}

.aic-feedback-input:focus {
  border-color: #0a44b9;
  outline: none;
}

.aic-feedback-btn {
  margin-top: 14px;
  padding: 12px 24px;
  background: #fff;
  border: 2px solid #0a44b9;
  border-radius: 10px;
  color: #0a44b9;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.aic-feedback-btn:hover:not(:disabled) {
  background: #0a44b9;
  color: #fff;
}

.aic-feedback-btn:disabled {
  border-color: #ccc;
  color: #999;
  cursor: not-allowed;
}

.proposal-feedback {
  background: #fff9f0;
  border-color: #ffd699;
}

.proposal-feedback h4 i {
  color: #d97706;
}

/* Modules */
.aic-module-section {
  margin-bottom: 28px;
}

.aic-module-section h4 {
  font-size: 16px;
  font-weight: 600;
  color: #0e1133;
  margin: 0 0 14px;
}

.aic-module-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
}

.aic-module-card {
  border: 2px solid #e8e8e8;
  border-radius: 14px;
  padding: 16px;
  cursor: pointer;
  background: #fff;
  transition: all 0.2s;
}

.aic-module-card:hover {
  border-color: #b0c4f5;
  transform: translateY(-2px);
}

.aic-module-card.selected {
  border-color: #0a44b9;
  background: #f0f4ff;
}

.aic-module-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.aic-module-header i:first-child {
  font-size: 20px;
  color: #0a44b9;
}

.aic-module-name {
  flex: 1;
  font-size: 15px;
  font-weight: 600;
  color: #0e1133;
}

.aic-module-check {
  font-size: 20px;
  color: #d5d5d5;
}

.aic-module-card.selected .aic-module-check {
  color: #0a44b9;
}

.aic-module-desc {
  font-size: 13px;
  color: #7a7a7a;
  margin: 0 0 10px;
  line-height: 1.5;
}

.aic-module-meta {
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: #999;
}

.aic-module-meta i {
  margin-right: 4px;
}

/* Estimate */
.aic-estimate {
  margin-bottom: 8px;
}

.aic-estimate-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 28px;
}

.aic-estimate-item {
  text-align: center;
  padding: 24px;
  background: linear-gradient(135deg, #f8f9fb 0%, #fff 100%);
  border-radius: 16px;
  border: 1px solid #e8e8e8;
}

.aic-estimate-item label {
  display: block;
  font-size: 13px;
  color: #7a7a7a;
  margin-bottom: 10px;
}

.aic-estimate-value {
  font-size: 20px;
  font-weight: 700;
  color: #0e1133;
}

.aic-estimate-value.price {
  color: #0a44b9;
}

.aic-estimate-breakdown {
  margin-bottom: 20px;
}

.aic-estimate-breakdown h4 {
  font-size: 16px;
  font-weight: 600;
  color: #0e1133;
  margin: 0 0 14px;
}

.aic-estimate-breakdown table {
  width: 100%;
  border-collapse: collapse;
}

.aic-estimate-breakdown th,
.aic-estimate-breakdown td {
  padding: 12px 14px;
  text-align: left;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
}

.aic-estimate-breakdown th {
  font-weight: 600;
  color: #7a7a7a;
  background: #f8f9fb;
}

.aic-estimate-breakdown td {
  color: #505056;
}

.aic-estimate-note {
  background: #fff7ed;
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 14px;
  color: #9a3412;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

/* Proposal */
.aic-proposal {
  background: #f8f9fb;
  border-radius: 14px;
  padding: 24px;
  margin-bottom: 8px;
  max-height: 500px;
  overflow-y: auto;
}

/* Markdown Viewer - 개선된 스타일 */
.aic-markdown-viewer {
  font-size: 14px;
  line-height: 1.7;
  color: #444;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.aic-markdown-viewer h1 {
  font-size: 20px;
  font-weight: 700;
  margin: 20px 0 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #0a44b9;
  color: #0e1133;
}

.aic-markdown-viewer h1:first-child {
  margin-top: 0;
}

.aic-markdown-viewer h2 {
  font-size: 16px;
  font-weight: 600;
  margin: 18px 0 10px;
  color: #0a44b9;
  padding-left: 10px;
  border-left: 3px solid #0a44b9;
}

.aic-markdown-viewer h3 {
  font-size: 14px;
  font-weight: 600;
  margin: 14px 0 8px;
  color: #333;
}

.aic-markdown-viewer h4 {
  font-size: 13px;
  font-weight: 600;
  margin: 12px 0 6px;
  color: #555;
}

.aic-markdown-viewer p {
  margin: 0 0 12px;
  color: #555;
}

.aic-markdown-viewer ul,
.aic-markdown-viewer ol {
  margin: 0 0 12px;
  padding-left: 20px;
}

.aic-markdown-viewer li {
  margin-bottom: 6px;
  color: #555;
}

.aic-markdown-viewer li::marker {
  color: #0a44b9;
}

.aic-markdown-viewer strong {
  font-weight: 600;
  color: #333;
}

.aic-markdown-viewer table {
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;
  font-size: 13px;
}

.aic-markdown-viewer th,
.aic-markdown-viewer td {
  border: 1px solid #e0e0e0;
  padding: 10px 12px;
  text-align: left;
}

.aic-markdown-viewer th {
  background: linear-gradient(135deg, #f8f9fb 0%, #f0f4ff 100%);
  font-weight: 600;
  color: #333;
}

.aic-markdown-viewer tr:nth-child(even) td {
  background: #fafbfc;
}

.aic-markdown-viewer code {
  background: #f5f5f7;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  color: #e53e3e;
}

.aic-markdown-viewer pre {
  background: #f8f9fb;
  padding: 12px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 12px 0;
}

.aic-markdown-viewer pre code {
  background: none;
  padding: 0;
  color: #333;
}

.aic-markdown-viewer blockquote {
  margin: 12px 0;
  padding: 10px 16px;
  background: #f0f7ff;
  border-left: 4px solid #0a44b9;
  border-radius: 0 8px 8px 0;
  color: #555;
}

.aic-markdown-viewer hr {
  border: none;
  border-top: 1px solid #e8e8e8;
  margin: 16px 0;
}

/* 전체보기 버튼 */
.aic-fullview-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 16px;
  padding: 12px;
  background: #fff;
  border: 2px solid #e8e8e8;
  border-radius: 10px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.aic-fullview-btn:hover {
  border-color: #0a44b9;
  color: #0a44b9;
  background: #f0f4ff;
}

.aic-fullview-btn i {
  font-size: 16px;
}

/* 기획서 전체보기 팝업 */
.proposal-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

.proposal-popup {
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.3);
  animation: popupSlideIn 0.3s ease-out;
}

@keyframes popupSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.proposal-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  border-bottom: 1px solid #e8e8e8;
  background: linear-gradient(135deg, #f8f9fb 0%, #fff 100%);
  border-radius: 20px 20px 0 0;
}

.proposal-popup-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0e1133;
  display: flex;
  align-items: center;
  gap: 10px;
}

.proposal-popup-header h3 i {
  color: #0a44b9;
}

.proposal-popup-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f7;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #666;
  font-size: 18px;
  transition: all 0.2s;
}

.proposal-popup-close:hover {
  background: #e8e8e8;
  color: #333;
}

.proposal-popup-body {
  flex: 1;
  overflow-y: auto;
  padding: 28px;
}

.proposal-popup-content {
  font-size: 15px;
  line-height: 1.8;
  color: #444;
}

.proposal-popup-content h1 {
  font-size: 24px;
  font-weight: 700;
  margin: 24px 0 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #0a44b9;
  color: #0e1133;
}

.proposal-popup-content h1:first-child {
  margin-top: 0;
}

.proposal-popup-content h2 {
  font-size: 18px;
  font-weight: 600;
  margin: 20px 0 12px;
  color: #0a44b9;
  padding-left: 12px;
  border-left: 4px solid #0a44b9;
}

.proposal-popup-content h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 16px 0 10px;
  color: #333;
}

.proposal-popup-content h4 {
  font-size: 14px;
  font-weight: 600;
  margin: 14px 0 8px;
  color: #555;
}

.proposal-popup-content p {
  margin: 0 0 14px;
  color: #555;
}

.proposal-popup-content ul,
.proposal-popup-content ol {
  margin: 0 0 14px;
  padding-left: 24px;
}

.proposal-popup-content li {
  margin-bottom: 8px;
  color: #555;
}

.proposal-popup-content li::marker {
  color: #0a44b9;
}

.proposal-popup-content strong {
  font-weight: 600;
  color: #333;
}

.proposal-popup-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  font-size: 14px;
}

.proposal-popup-content th,
.proposal-popup-content td {
  border: 1px solid #e0e0e0;
  padding: 12px 14px;
  text-align: left;
}

.proposal-popup-content th {
  background: linear-gradient(135deg, #f8f9fb 0%, #f0f4ff 100%);
  font-weight: 600;
  color: #333;
}

.proposal-popup-content tr:nth-child(even) td {
  background: #fafbfc;
}

.proposal-popup-content code {
  background: #f5f5f7;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
  color: #e53e3e;
}

.proposal-popup-content pre {
  background: #f8f9fb;
  padding: 14px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 14px 0;
}

.proposal-popup-content pre code {
  background: none;
  padding: 0;
  color: #333;
}

.proposal-popup-content blockquote {
  margin: 14px 0;
  padding: 12px 18px;
  background: #f0f7ff;
  border-left: 4px solid #0a44b9;
  border-radius: 0 8px 8px 0;
  color: #555;
}

.proposal-popup-content hr {
  border: none;
  border-top: 1px solid #e8e8e8;
  margin: 20px 0;
}

.proposal-popup-footer {
  padding: 16px 28px;
  border-top: 1px solid #e8e8e8;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #fafbfc;
  border-radius: 0 0 20px 20px;
}

.proposal-popup-footer .aic-primary-btn {
  margin-top: 0;
  width: auto;
  padding: 12px 24px;
}

/* 기획서 이력 카드 */
.aic-history-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  margin-top: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8e8e8;
}

.aic-history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.aic-history-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0e1133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.aic-history-header h3 i {
  color: #0a44b9;
}

.aic-history-count {
  font-size: 13px;
  color: #7a7a7a;
  background: #f5f5f7;
  padding: 4px 10px;
  border-radius: 12px;
}

.aic-history-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.aic-history-item-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #f8f9fb;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  transition: all 0.2s;
}

.aic-history-item-card:hover {
  border-color: #d0e3ff;
  background: #f0f7ff;
}

.aic-history-item-info {
  flex: 1;
  min-width: 0;
}

.aic-history-item-title {
  font-size: 15px;
  font-weight: 600;
  color: #0e1133;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.aic-history-item-meta {
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: #7a7a7a;
  margin-bottom: 6px;
}

.aic-history-item-meta span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.aic-history-item-meta i {
  font-size: 11px;
}

.aic-history-item-summary {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
}

.aic-history-item-actions {
  display: flex;
  gap: 8px;
  margin-left: 16px;
  flex-shrink: 0;
}

.aic-history-view-btn,
.aic-history-use-btn,
.aic-history-delete-btn {
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.aic-history-view-btn {
  background: #fff;
  border: 1px solid #e0e0e0;
  color: #666;
}

.aic-history-view-btn:hover {
  border-color: #0a44b9;
  color: #0a44b9;
}

.aic-history-use-btn {
  background: #0a44b9;
  border: none;
  color: #fff;
}

.aic-history-use-btn:hover {
  background: #083a9e;
}

.aic-history-delete-btn {
  background: #fff;
  border: 1px solid #e0e0e0;
  color: #999;
  padding: 8px 10px;
}

.aic-history-delete-btn:hover {
  border-color: #ef4444;
  color: #ef4444;
  background: #fef2f2;
}

/* History Toggle (피드백 히스토리) */
.aic-history-toggle {
  margin-top: 20px;
}

.aic-history-btn {
  background: none;
  border: none;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
}

.aic-history-btn:hover {
  color: #0a44b9;
}

.aic-history-list {
  margin-top: 14px;
  padding: 16px;
  background: #f8f9fb;
  border-radius: 10px;
  max-height: 250px;
  overflow-y: auto;
}

.aic-history-item {
  padding: 12px 0;
  border-bottom: 1px solid #e8e8e8;
}

.aic-history-item:last-child {
  border-bottom: none;
}

.aic-history-label {
  font-size: 13px;
  font-weight: 600;
  color: #0a44b9;
  margin-bottom: 6px;
}

.aic-history-content {
  font-size: 14px;
  color: #555;
  line-height: 1.5;
}

/* Summary */
.aic-summary {
  background: #f8f9fb;
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 20px;
}

.aic-summary h5 {
  font-size: 14px;
  font-weight: 600;
  color: #7a7a7a;
  margin: 0 0 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.aic-summary-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 15px;
}

.aic-summary-row span {
  color: #7a7a7a;
}

.aic-summary-row strong {
  color: #0e1133;
  font-weight: 600;
}

/* Loading Overlay */
.aic-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.aic-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e8e8e8;
  border-top-color: #0a44b9;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 767px) {
  .aic-header-inner {
    flex-wrap: wrap;
    gap: 12px;
  }

  .aic-header-title {
    order: 3;
    width: 100%;
    text-align: center;
  }

  .aic-header-title h1 {
    font-size: 18px;
  }

  .aic-back-home span {
    display: none;
  }

  .aic-main {
    padding: 20px 16px 40px;
  }

  .aic-steps {
    padding: 16px;
    gap: 2px;
  }

  .aic-step {
    padding: 6px 8px;
  }

  .aic-step-num {
    width: 28px;
    height: 28px;
    font-size: 13px;
  }

  .aic-step-label {
    display: none;
  }

  .aic-step.active .aic-step-label {
    display: block;
    font-size: 12px;
  }

  .aic-step-line {
    width: 16px;
    margin: 0 2px;
  }

  .aic-card {
    padding: 24px 20px;
    border-radius: 16px;
  }

  .aic-card-title {
    font-size: 20px;
  }

  .aic-row {
    flex-direction: column;
    gap: 0;
  }

  .aic-estimate-summary {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .aic-module-grid {
    grid-template-columns: 1fr;
  }

  .aic-btn-row {
    flex-direction: column;
  }

  .aic-btn-row .aic-secondary-btn {
    order: 2;
  }

  /* 모바일 팝업 스타일 */
  .proposal-popup-overlay {
    padding: 10px;
  }

  .proposal-popup {
    max-height: 95vh;
    border-radius: 16px;
  }

  .proposal-popup-header {
    padding: 16px 20px;
    border-radius: 16px 16px 0 0;
  }

  .proposal-popup-header h3 {
    font-size: 16px;
  }

  .proposal-popup-body {
    padding: 20px;
  }

  .proposal-popup-content h1 {
    font-size: 20px;
  }

  .proposal-popup-content h2 {
    font-size: 16px;
  }

  .proposal-popup-footer {
    padding: 14px 20px;
    border-radius: 0 0 16px 16px;
    flex-direction: column;
    gap: 10px;
  }

  .proposal-popup-footer .aic-secondary-btn,
  .proposal-popup-footer .aic-primary-btn {
    width: 100%;
    justify-content: center;
  }

  /* 기획서 이력 모바일 */
  .aic-history-card {
    padding: 16px;
  }

  .aic-history-item-card {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .aic-history-item-actions {
    margin-left: 0;
    justify-content: flex-end;
  }

  .aic-history-item-meta {
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style>
